import React, { useState } from 'react';
import Table, { TableAction, TableColumn } from '../../components/common/Table';
import { MemberEndorsementDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors } from '../../utils/displayUtils';
import { getRoles } from '../../utils/tokenUtils';
import FileIcons from '../common/FileIcons';
import Badge from '../common/Badge';
import Loader from '../common/Loader';
import { Mail, FileText } from 'lucide-react';
import { FileViewer } from '../common/FileViewer';
import createEndorsementActions from '../actionMenus/EndorsementActions';
import { EndorsementLicenceTypeConstants } from '../../constants/EndorsementLicenceTypeConstants';
import { EndorsementStatusConstants } from '../../constants/EndorsementStatusConstants';
import { useMemberEndorsementApi } from '../../hooks/useMemberEndorsementApi';
import FeedbackBubble from '../common/FeedbackBubble';
import MemberNoteIcons from '../common/MemberNoteIcons';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import ReviewPanel from '../reviewPanels/ReviewPanel';
import { FirearmMotivationTypeConstants } from '../../constants/FirearmMotivationTypeConstants';
import { FellowConstants } from '../../constants/FellowConstants';
import { toTitleCase } from '../../utils/stringUtils';

interface MemberEndorsementTableProps {
  data: MemberEndorsementDTO[];
  handleViewMemberDetails: (row: MemberEndorsementDTO) => void;
  handleEdit: (row: MemberEndorsementDTO) => void;
  handleDelete: (row: MemberEndorsementDTO) => Promise<void>;
  handleSendEmail: (row: MemberEndorsementDTO) => void;
  handleViewAllEmails: (row: MemberEndorsementDTO) => void;
  handleViewRecordEmails: (row: MemberEndorsementDTO) => void;
  handleRestore: (row: MemberEndorsementDTO) => Promise<void>;
  handleApprove: (row: MemberEndorsementDTO) => void;
  handleChangeStatus: (row: MemberEndorsementDTO) => void;
  handleImpersonateMemberLogin: (row: MemberEndorsementDTO) => void;
  handleGenerateCertificate: (row: MemberEndorsementDTO) => void;
  handleAddNote: (row: MemberEndorsementDTO) => void;
  handleViewNoteHistory: (row: MemberEndorsementDTO) => void;
  handleMemberClick?: (memberId: string) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
}

const MemberEndorsementTable: React.FC<MemberEndorsementTableProps> = ({
  data,
  handleViewMemberDetails,
  handleEdit,
  handleDelete,
  handleSendEmail,
  handleViewAllEmails,
  handleViewRecordEmails,
  handleRestore,
  handleApprove,
  handleChangeStatus,
  handleImpersonateMemberLogin,
  handleGenerateCertificate,
  handleAddNote,
  handleViewNoteHistory,
  handleMemberClick,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
}) => {
  const roles = getRoles();
  const isAdmin = roles?.includes('Admin');
  const { getDeclaration, getFeedback } = useMemberEndorsementApi();
  const [viewingFile, setViewingFile] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [generatingDeclarationId, setGeneratingDeclarationId] = useState<string | null>(null);

  const allCurrentPageIds = data.map(row => row.id).filter((id): id is string => !!id);
  const allSelected = allCurrentPageIds.length > 0 && allCurrentPageIds.every(id => selectedIds.has(id));
  const someSelected = allCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

  // Check if status is locked (Approved, Approved with Feedback, Declined, or Decline with Feedback)
  const isStatusLocked = (row: MemberEndorsementDTO): boolean => {
    const lockedStatuses = [
      EndorsementStatusConstants.APPROVED.toLowerCase(),
      EndorsementStatusConstants.APPROVED_WITH_FEEDBACK.toLowerCase(),
      EndorsementStatusConstants.DECLINED.toLowerCase(),
      EndorsementStatusConstants.DECLINE_WITH_FEEDBACK.toLowerCase(),
    ];
    return lockedStatuses.includes(row.statusId?.toLowerCase() || '');
  };

  const handleGenerateDeclaration = async (row: MemberEndorsementDTO) => {
    setGeneratingDeclarationId(row.id ?? null);
    try {
      const declarationUrl = await getDeclaration(row.id ?? '');
      const filename = `Endorsement-Declaration(${row.id ?? ''}).pdf`;
      setViewingFile({
        name: filename,
        url: declarationUrl,
      });
    } catch (error) {
      console.error('Error generating declaration:', error);
    } finally {
      setGeneratingDeclarationId(null);
    }
  };

  const columns: TableColumn[] = [
    ...(onSelectRow && onSelectAll
      ? [
        {
          label: (
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll?.(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
          key: 'checkbox',
          render: (row: MemberEndorsementDTO) => (
            <input
              type="checkbox"
              checked={selectedIds.has(row.id ?? '')}
              onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
        },
      ]
      : []),
    ...(roles?.includes('Admin')
      ? [
        {
          label: 'Member',
          key: 'member',
          sortable: true,
          sortKey: 'FirstName',
          showInReview: true,
          render: (row: MemberEndorsementDTO) => (
            <div
              onClick={() => handleMemberClick?.(row.member?.id ?? '')}
              className="flex flex-col gap-1 cursor-pointer"
            >
              <span>
                {row.member?.firstName} {row.member?.lastName}
              </span>
              <span>{row.member?.idPassportNumber ?? ''}</span>
              <MemberNoteIcons member={row.member} tableContext="endorsements" recordId={row.id} />
            </div>
          ),
        },
        {
          label: 'Renew',
          key: 'renewalSubmitDate',
          render: (row: MemberEndorsementDTO) => (
            <span>{convertToUserTime(row.member?.paidUpTo?.toString() ?? '', true)}</span>
          ),
          sortable: true,
          sortKey: 'MemberPaidUpTo',
          showInReview: true,
        },
        {
          label: 'Serial',
          key: 'firearmSerial',
          showInReview: true,
          render: (row: MemberEndorsementDTO) => {
            return (
              <div className="flex flex-col">
                <span>F:{row.firearm?.frameSerial}</span>
                <span>B:{row.firearm?.barrelSerial}</span>
              </div>
            );
          },
        },
      ]
      : []),

    {
      label: 'Firearm',
      key: 'firearm',
      showInReview: true,
      render: (row: MemberEndorsementDTO) => {
        const makeTitle = row.firearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? row.firearm?.firearmMakeOther : row.firearm?.firearmMake?.title ?? '-';
        const caliberTitle = row.firearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.firearm?.caliberOther : row.firearm?.caliber?.title ?? '-';
        return (
          <div className="flex flex-col">
            <span>T: {row.firearm?.firearmType?.title ?? '-'}</span>
            <span>C: {caliberTitle}</span>
            <span>A: {row.firearm?.firearmAction?.title ?? '-'}</span>
            <span>M: {makeTitle}</span>
          </div>
        )
      },
      sortable: true,
      sortKey: 'FirearmTypeTitle',
    },

    ...(roles?.includes('User')
      ? [
        {
          label: 'Licence',
          key: 'licenceType',
          render: (row: MemberEndorsementDTO) => <span>{row.licenceType?.title}</span>,
          sortable: true,
          sortKey: 'LicenceTypeTitle',
        },
      ]
      : []),

    ...(roles?.includes('User')
      ? [
        {
          label: 'Frame / Receiver Serial',
          key: 'firearmFrameSerial',
          render: (row: MemberEndorsementDTO) => <span>{row.firearm?.frameSerial}</span>,
        },
        {
          label: 'Barrel Serial',
          key: 'firearmBarrelSerial',
          render: (row: MemberEndorsementDTO) => <span>{row.firearm?.barrelSerial}</span>,
        },
      ]
      : []),

    ...(roles?.includes('Admin')
      ? [
        {
          label: 'Purpose',
          key: 'firearmPurpose',
          showInReview: true,
          render: (row: MemberEndorsementDTO) => (
            <div className="flex flex-col">
              <span>FCA: {row.member?.memberCategory?.titleShort ?? '-'}</span>
              <span>P: {row.firearm?.purposeType?.title ?? '-'}</span>
            </div>
          ),
          sortable: true,
          sortKey: 'FirearmPurposeTitle',
        },
        {
          label: 'Licence',
          key: 'licenceType',
          showInReview: true,
          render: (row: MemberEndorsementDTO) => <span>{row.licenceType?.title}</span>,
          sortable: true,
          sortKey: 'LicenceTypeTitle',
        },
      ]
      : []),

    {
      label: 'Motivation',
      key: 'files',
      render: (row: MemberEndorsementDTO) => (
        <FileIcons
          files={[
            {
              id: row.motivationFile1Id,
              label: 'Motivation File 1',
            },
            ...(row.motivationFile2Id
              ? [
                {
                  id: row.motivationFile2Id,
                  label: 'Additional File 1',
                },
              ]
              : []),
            ...(row.motivationFile3Id
              ? [
                {
                  id: row.motivationFile3Id,
                  label: 'Additional File 2',
                },
              ]
              : []),
            ...(row.motivationFile4Id
              ? [
                {
                  id: row.motivationFile4Id,
                  label: 'Additional File 3',
                },
              ]
              : []),
          ].filter((file) => file.id != null)}
          renderSidePanel={isAdmin ? () => <ReviewPanel row={row} columns={columns} /> : undefined}
        />
      ),
    },

    {
      label: 'Declaration',
      key: 'declaration',
      render: (row: MemberEndorsementDTO) => {
        // Only show PDF icon for renewal licence type
        if (
          row.licenceTypeId?.toUpperCase() ===
          EndorsementLicenceTypeConstants.RENEWAL_OF_EXISTING_LICENCE.toUpperCase()
        ) {
          const isGenerating = generatingDeclarationId === row.id;
          return isGenerating ? (
            <Loader size='sm' center={false} />
          ) : (
            <button
              onClick={() => handleGenerateDeclaration(row)}
              className="text-secondary hover:text-primary transition-colors hover:cursor-pointer"
              title="Generate Declaration"
            >
              <FileText size={20} />
            </button>
          );
        }
        return <span>-</span>;
      },
    },

    ...(roles?.includes('Admin')
      ? [
        {
          label: 'Author',
          key: 'motivationType',
          showInReview: true,
          render: (row: MemberEndorsementDTO) => {
            const base = row.motivationType?.colourGrading?.colour ?? '#ffffffff';
            const { bg, text } = getBadgeColors(base, 0.18, 0.4);

            if (
              row.motivationTypeId === FirearmMotivationTypeConstants.SOMEONE_ELSE &&
              row.fellow?.id != FellowConstants.NOT_ON_LIST &&
              (row.fellow?.name || row.fellow?.companyName)
            ) {
              return (
                <div
                  style={{ backgroundColor: bg, color: text }}
                  className="flex flex-col min-w-0 px-2 py-1 rounded"
                >
                  <span className="text-xs font-semibold truncate">
                    {row.fellow?.name ? toTitleCase(row.fellow.name) : ''}
                  </span>
                  {row.fellow?.companyName && (
                    <span className="text-xs opacity-75 truncate">
                      {toTitleCase(row.fellow.companyName)}
                    </span>
                  )}
                </div>
              );
            }

            const label = row.motivationType?.title || 'Unknown';
            return base ? (
              <Badge
                label={label}
                variant="custom"
                customBgColor={bg}
                customTextColor={text}
              />
            ) : (
              <span>{label}</span>
            );
          },
        },
        {
          label: 'Membership',
          key: 'memberType',
          showInReview: true,
          render: (row: MemberEndorsementDTO) => (
            <span>{row.member?.memberType?.titleShort}</span>
          ),
          sortable: true,
          sortKey: 'MemberTypeTitleShort',
        },
        {
          label: 'Sent',
          key: 'approvalSent',
          render: (row: MemberEndorsementDTO) => (
            <Mail fill={row.statusEmailSentDate ? 'green' : 'orange'} stroke={'white'} />
          ),
        },
        {
          label: 'FB',
          key: 'feedback',
          render: (row: MemberEndorsementDTO) => (
            <FeedbackBubble
              hasFeedback={!!row.feedbackFileId}
              fetchFeedback={() => getFeedback(row.id!)}
            />
          ),
        },
      ]
      : []),

    {
      label: 'Status',
      key: 'statusId',
      sortable: true,
      sortKey: 'EndorsementStatusTitle',
      showInReview: true,
      render: (row: MemberEndorsementDTO) => {
        const base = row.status?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.status?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.status?.title || 'Unknown'}</span>
        );
      },
    },

    ...(roles?.includes('User')
      ? [
        {
          label: 'Applied',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? '', false),
        },
      ]
      : []),

    ...(roles?.includes('Admin')
      ? [
        {
          label: 'Updated',
          key: 'statusUpdateDate',
          sortable: true,
          sortKey: 'UpdatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? '', false),
        },
        {
          label: 'Created',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? '', false),
        },
      ]
      : []),
  ];

  return (
    <>
      <Table
        columns={columns}
        data={data}
        actions={{
          ...(!isAdmin && {
            extra: (row) => {
              // For non-admin users, show edit action conditionally
              if (isStatusLocked(row)) {
                return [];
              }
              return [
                {
                  label: 'Edit Endorsement',
                  onClick: () => handleEdit(row),
                },
              ];
            },
          }),
          ...(isAdmin && {
            onEdit: handleEdit,
            extra: (row) =>
              createEndorsementActions({
                onViewMemberDetails: handleViewMemberDetails,
                onView: handleEdit,
                onDelete: handleDelete,
                onSendEmail: handleSendEmail,
                onViewAllEmails: handleViewAllEmails,
                onViewRecordEmails: handleViewRecordEmails,
                onRestore: handleRestore,
                onApprove: handleApprove,
                onChangeStatus: handleChangeStatus,
                onImpersonateMemberLogin: handleImpersonateMemberLogin,
                onHandleGenerateCertificate: handleGenerateCertificate,
                onAddNote: handleAddNote,
                onViewNoteHistory: handleViewNoteHistory,
              })(row),
          }),
        }}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSort={onSort}
      />

      {viewingFile && (
        <FileViewer
          fileName={viewingFile.name}
          sasUrl={viewingFile.url}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  );
};

export default MemberEndorsementTable;
